import { useTheme } from "@mui/material";
import { keyframes, styled } from "@mui/material/styles";

const ANIMATION_DURATION = "2s";
const RING_EASING = "cubic-bezier(0.77, 0, 0.175, 1)";

const mainArcRotation = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Replicates the original SVGator transform chain translate(origin) rotate(angle) translate(offset),
// where the origin's Y wobbles 55 -> 52 -> 58 -> 55 while the angle goes 4 -> 0 -> 4 degrees.
const innerAssemblyMotion = keyframes`
  0% { transform: translate(55.354242px, 55px) rotate(4deg) translate(-55.354242px, -54.000002px); }
  30% { transform: translate(55.354242px, 52px) rotate(1.6deg) translate(-55.354242px, -54.000002px); }
  50% { transform: translate(55.354242px, 55px) rotate(0deg) translate(-55.354242px, -54.000002px); }
  70% { transform: translate(55.354242px, 58px) rotate(1.6deg) translate(-55.354242px, -54.000002px); }
  100% { transform: translate(55.354242px, 55px) rotate(4deg) translate(-55.354242px, -54.000002px); }
`;

const secondaryMarkRotation = keyframes`
  0% { transform: translate(37.894597px, 54.005098px) rotate(7deg) translate(-37.890666px, -54.005098px); }
  50% { transform: translate(37.894597px, 54.005098px) rotate(-7deg) translate(-37.890666px, -54.005098px); }
  100% { transform: translate(37.894597px, 54.005098px) rotate(7deg) translate(-37.890666px, -54.005098px); }
`;

const firstRingRotation = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const firstRingOpacity = keyframes`
  0% { opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { opacity: 0; }
`;

const secondRingRotation = keyframes`
  from { transform: rotate(-360deg); }
  to { transform: rotate(360deg); }
`;

const secondRingOpacity = keyframes`
  0% { opacity: 0; }
  30% { opacity: 1; }
  70% { opacity: 1; }
  100% { opacity: 0; }
`;

const AnimatedSvg = styled("svg")({
  "& .mainArc": {
    transformBox: "view-box",
    transformOrigin: "55px 55px",
    animation: `${mainArcRotation} ${ANIMATION_DURATION} linear infinite`,
  },
  "& .innerAssembly": {
    transformBox: "view-box",
    transformOrigin: "0 0",
    animation: `${innerAssemblyMotion} ${ANIMATION_DURATION} linear infinite`,
  },
  "& .secondaryMark": {
    transformBox: "view-box",
    transformOrigin: "0 0",
    animation: `${secondaryMarkRotation} ${ANIMATION_DURATION} linear infinite`,
  },
  "& .firstRing": {
    transformBox: "view-box",
    transformOrigin: "65px 65px",
    animation: `${firstRingRotation} ${ANIMATION_DURATION} ${RING_EASING} infinite`,
  },
  "& .firstRing ellipse": {
    animation: `${firstRingOpacity} ${ANIMATION_DURATION} ${RING_EASING} infinite`,
  },
  "& .secondRing": {
    transformBox: "view-box",
    transformOrigin: "65px 65px",
    animation: `${secondRingRotation} ${ANIMATION_DURATION} ${RING_EASING} infinite`,
  },
  "& .secondRing ellipse": {
    animation: `${secondRingOpacity} ${ANIMATION_DURATION} ${RING_EASING} infinite`,
  },
});

const InlineLoaderSvg = () => {
  const theme = useTheme();
  const trackColor = theme.palette.mode === "dark" ? "rgba(217, 237, 255, 0.2)" : "rgb(237,240,242)";
  const gradientEndColor = trackColor;

  return (
    <AnimatedSvg
      id="eM5oBvJqysb1"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 130 130"
      shapeRendering="geometricPrecision"
      textRendering="geometricPrecision"
    >
      <defs>
        <linearGradient
          id="eM5oBvJqysb8-fill"
          x1="82.999991"
          y1="48.5"
          x2="82.000009"
          y2="6.5"
          spreadMethod="pad"
          gradientUnits="userSpaceOnUse"
          gradientTransform="matrix(1 0 0 1 0 0)"
        >
          <stop id="eM5oBvJqysb8-fill-0" offset="35.1818%" stopColor="rgb(0,169,102)" />
          <stop id="eM5oBvJqysb8-fill-1" offset="87.0019%" stopColor={gradientEndColor} />
        </linearGradient>
      </defs>
      <g id="eM5oBvJqysb2" transform="matrix(1 0 0 1 10 10)">
        <g id="eM5oBvJqysb3">
          <g id="eM5oBvJqysb4" mask="url(#eM5oBvJqysb6)">
            <path
              id="eM5oBvJqysb5"
              d="M110,55C110,85.3757,85.3757,110,55,110C24.6243,110,0,85.3757,0,55C0,24.6243,24.6243,0,55,0C85.3757,0,110,24.6243,110,55ZM5.05724,55C5.05724,82.5826,27.4174,104.943,55,104.943C82.5826,104.943,104.943,82.5826,104.943,55C104.943,27.4174,82.5826,5.05724,55,5.05724C27.4174,5.05724,5.05724,27.4174,5.05724,55Z"
              fill={trackColor}
              stroke={trackColor}
              strokeWidth="10"
            />
            <mask id="eM5oBvJqysb6" style={{ maskType: "luminance" }}>
              <path
                id="eM5oBvJqysb7"
                d="M110,55C110,85.3757,85.3757,110,55,110C24.6243,110,0,85.3757,0,55C0,24.6243,24.6243,0,55,0C85.3757,0,110,24.6243,110,55ZM5.05724,55C5.05724,82.5826,27.4174,104.943,55,104.943C82.5826,104.943,104.943,82.5826,104.943,55C104.943,27.4174,82.5826,5.05724,55,5.05724C27.4174,5.05724,5.05724,27.4174,5.05724,55Z"
                fill="rgb(255,255,255)"
                stroke="none"
                strokeWidth="1"
              />
            </mask>
          </g>
        </g>
        <g className="mainArc">
          <path
            id="eM5oBvJqysb8"
            d="M107.471,55C108.868,55,110.006,53.8673,109.942,52.4722C109.649,46.1128,108.254,39.8458,105.813,33.9524C103.049,27.2795,98.9981,21.2163,93.8909,16.1091C88.7837,11.0019,82.7205,6.95063,76.0476,4.18663C69.3747,1.42262,62.2227,0,55,0L55,5.05724C61.5586,5.05724,68.0529,6.34905,74.1123,8.85891C80.1716,11.3688,85.6772,15.0475,90.3149,19.6851C94.9525,24.3228,98.6312,29.8284,101.141,35.8877C103.328,41.1675,104.59,46.7775,104.879,52.4724C104.949,53.8672,106.075,55,107.471,55L107.471,55Z"
            transform="matrix(1 0 0 1 0.000002 0.000006)"
            fill="url(#eM5oBvJqysb8-fill)"
            stroke="none"
            strokeWidth="1"
          />
        </g>
        <g id="eM5oBvJqysb9" className="innerAssembly">
          <path
            id="eM5oBvJqysb10"
            d="M86.6772,53.8643L86.3593,53.1631C80.2065,39.6724,66.2297,30.2082,52.2483,30.0001C51.994,29.9955,51.7852,30.2037,51.7852,30.457L51.7852,33.5967C51.7852,33.8455,51.985,34.04,52.2302,34.0445C57.0753,34.1215,61.9204,35.456,66.3568,37.7723C62.5107,42.3054,60.5854,48.6571,61.1666,55.1627C61.6752,60.8313,64.0273,65.8756,67.7509,69.4676C62.9466,72.2499,57.5884,73.8695,52.2347,73.9555C51.985,73.96,51.7897,74.159,51.7897,74.4033L51.7897,77.543C51.7897,77.7963,51.9986,78.0045,52.2529,77.9999C66.2342,77.7963,80.211,68.3276,86.3639,54.8369L86.6817,54.1357C86.7181,54.0498,86.7181,53.9502,86.6772,53.8643ZM82.1954,54.1493C79.6298,59.3248,75.7882,63.79,71.2428,67.1649C67.8326,64.2333,65.6621,59.8269,65.2125,54.8008C64.7039,49.1141,66.4476,43.6083,69.8941,39.8805C75.0299,43.3459,79.38,48.1731,82.1954,53.8507C82.2408,53.9457,82.2408,54.0588,82.1954,54.1493Z"
            fill="rgb(0,168,102)"
            stroke="none"
            strokeWidth="1"
          />
          <path
            id="eM5oBvJqysb11"
            className="secondaryMark"
            d="M43.5433,45.6854C43.4662,45.8212,43.5116,45.9976,43.6478,46.0745L46.5267,47.7167C46.7447,47.8389,47.0217,47.7665,47.1443,47.5493L51.7215,39.5961C51.8758,39.3247,51.7169,38.9854,51.4127,38.9265L24.5398,33.8325C24.1811,33.7646,23.8905,34.1266,24.0403,34.4613L32.7088,53.7291C32.7633,53.8468,32.7633,53.978,32.7088,54.0956L24.0539,73.5489C23.9041,73.8837,24.1947,74.2456,24.5534,74.1777L51.4127,69.0837C51.7169,69.0249,51.8758,68.6856,51.7215,68.4141L47.1488,60.4609C47.0262,60.2437,46.7447,60.1668,46.5312,60.2935L43.6024,61.9674C43.4934,62.0307,43.4571,62.1664,43.5161,62.275L45.4868,65.7042C45.5867,65.8761,45.4868,66.0888,45.2916,66.1295L31.0923,68.8213C30.8652,68.8665,30.6836,68.6358,30.7744,68.4232L36.9863,54.4575C37.1407,54.1046,37.1407,53.702,36.9863,53.3536L30.7971,39.5961C30.7018,39.3835,30.8834,39.1527,31.115,39.198L45.2916,41.8853C45.4868,41.9214,45.5867,42.1386,45.4868,42.3105L43.5433,45.6854Z"
            fill="rgb(0,168,102)"
            stroke="none"
            strokeWidth="1"
          />
          <path
            id="eM5oBvJqysb12"
            d="M70.0575,50.5603C71.3691,50.5603,72.4324,49.5009,72.4324,48.1942C72.4324,46.8874,71.3691,45.8281,70.0575,45.8281C68.7459,45.8281,67.6826,46.8874,67.6826,48.1942C67.6826,49.5009,68.7459,50.5603,70.0575,50.5603Z"
            fill="rgb(0,168,102)"
            stroke="none"
            strokeWidth="1"
          />
        </g>
      </g>
      <g className="firstRing">
        <ellipse
          id="eM5oBvJqysb13"
          rx="43.618614"
          ry="43.618614"
          transform="matrix(1.088103 0 0 1.088103 65 65)"
          opacity="0"
          fill="none"
          stroke={trackColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeDashoffset="0.2"
          strokeDasharray="0,4"
        />
      </g>
      <g className="secondRing">
        <ellipse
          id="eM5oBvJqysb14"
          rx="43.618614"
          ry="43.618614"
          transform="matrix(1.330058 0 0 1.330054 65 65)"
          opacity="0"
          fill="none"
          stroke={trackColor}
          strokeWidth="1"
          strokeLinecap="round"
          strokeDashoffset="-0.2"
          strokeDasharray="45"
        />
      </g>
    </AnimatedSvg>
  );
};

export default InlineLoaderSvg;
